/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { PrimaryButton } from "../buttons/PrimaryButton";
import { getWeekRange } from "../../services/dateService";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
  Tooltip,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import isoWeek from "dayjs/plugin/isoWeek";
import axios from "axios";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { useUserStats } from "../../store/useUserStats";
import InformationIcon from "../../assets/icons/info/information.png";

// Extend dayjs with plugins
dayjs.extend(weekOfYear);
dayjs.extend(quarterOfYear);
dayjs.extend(isoWeek);

interface Props {
  periodProp?: "all" | "yearly" | "quarterly" | "monthly" | "weekly";
  yearProp?: number;
  quarterProp?: "Q1" | "Q2" | "Q3" | "Q4";
  monthProp?: number;
  weekProp?: Date;
  selectedFiltersProp?: string[];
  selectedStoresProp?: string[];
  showTargets?: boolean;
  /** When true, shows From/To filters (period: month/week/quarter/year) instead of legacy period dropdown */
  useFromToFilters?: boolean;
}

const DEFAULT_WEEK = new Date();
const EMPTY_STRING_ARRAY: string[] = [];

const monthOptions = [
  { value: 1, label: "Jan" },
  { value: 2, label: "Feb" },
  { value: 3, label: "Mar" },
  { value: 4, label: "Apr" },
  { value: 5, label: "May" },
  { value: 6, label: "Jun" },
  { value: 7, label: "Jul" },
  { value: 8, label: "Aug" },
  { value: 9, label: "Sep" },
  { value: 10, label: "Oct" },
  { value: 11, label: "Nov" },
  { value: 12, label: "Dec" },
];

const quarterOptions = [
  { value: 1, label: "Q1" },
  { value: 2, label: "Q2" },
  { value: 3, label: "Q3" },
  { value: 4, label: "Q4" },
];

export const CumulativeUsersOverTime = React.memo(
  ({
    periodProp = "all",
    yearProp = dayjs().year(),
    quarterProp = "Q1",
    monthProp = dayjs().month(),
    weekProp = DEFAULT_WEEK,
    selectedFiltersProp = EMPTY_STRING_ARRAY,
    selectedStoresProp = EMPTY_STRING_ARRAY,
    showTargets = false,
    useFromToFilters = false,
  }: Props) => {
    /** Refs */
    const snapshotRef = useRef(null);
    /** Hooks */
    const {
      cumulativeUsersOverTime,
      getCumulativeUsersOverTime,
      isCumulativeUsersOverTimeLoading,
    } = useUserStats();
    const [_, takeScreenshot] = useScreenshot();
    const [isCsvDownloading, setIsCsvDownloading] = useState(false);
    /** State */
    const [period, setPeriod] = useState<
      "all" | "yearly" | "quarterly" | "monthly" | "weekly"
    >(periodProp);
    const [year, setYear] = useState<number>(yearProp);
    const [quarter, setQuarter] = useState<"Q1" | "Q2" | "Q3" | "Q4">(
      quarterProp
    );
    const [month, setMonth] = useState<number>(monthProp);
    const [week, setWeek] = useState<Date>(weekProp);
    const [downloadsTarget, setDownloadsTarget] = useState<number>(23500);
    const [regularDroppersTarget, setRegularDroppersTarget] =
      useState<number>(13000);

    // From/To filter state (when useFromToFilters)
    const [fromToPeriod, setFromToPeriod] = useState<
      "month" | "week" | "quarter" | "year"
    >("month");
    const [startYear, setStartYear] = useState(() =>
      dayjs().subtract(12, "month").year()
    );
    const [startMonth, setStartMonth] = useState(
      () => dayjs().subtract(12, "month").month() + 1
    );
    const [endYear, setEndYear] = useState(() => dayjs().year());
    const [endMonth, setEndMonth] = useState(() => dayjs().month() + 1);
    const [startWeek, setStartWeek] = useState(() =>
      dayjs().subtract(12, "month").isoWeek()
    );
    const [startQuarter, setStartQuarter] = useState(1);
    const [endWeek, setEndWeek] = useState(() => dayjs().isoWeek());
    const [endQuarter, setEndQuarter] = useState(4);

    /** Constants */
    const { startOfWeek, endOfWeek } = getWeekRange(week);
    const currentYear = dayjs().year();
    const yearOptions = Array.from(
      { length: dayjs().year() - 2023 },
      (_, i) => dayjs().year() - i
    );
    const startMonthOptions = useMemo(() => {
      const cy = dayjs().year();
      const cm = dayjs().month() + 1;
      return startYear === cy
        ? monthOptions.filter((m) => m.value <= cm)
        : monthOptions;
    }, [startYear]);
    const endMonthOptions = useMemo(() => {
      const cy = dayjs().year();
      const cm = dayjs().month() + 1;
      return endYear === cy
        ? monthOptions.filter((m) => m.value <= cm)
        : monthOptions;
    }, [endYear]);
    const getWeekOptions = (y: number) => {
      const lastDay = dayjs(`${y}-12-31`);
      const totalWeeks = lastDay.isoWeek() === 1 ? 52 : lastDay.isoWeek();
      const currentWeek = dayjs().isoWeek();
      const currentY = dayjs().year();
      return Array.from({ length: totalWeeks }, (_, i) => {
        const w = i + 1;
        const start = dayjs().year(y).isoWeek(w).startOf("isoWeek");
        const end = dayjs().year(y).isoWeek(w).endOf("isoWeek");
        return {
          value: w,
          label: `Week ${w}: ${start.format("MMM DD")} - ${end.format("MMM DD")}`,
          disabled: y === currentY && w > currentWeek,
        };
      });
    };
    const startWeekOptions = useMemo(
      () => getWeekOptions(startYear),
      [startYear]
    );
    const endWeekOptions = useMemo(() => getWeekOptions(endYear), [endYear]);

    /** Callbacks */
    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        // Create a link to download the image
        const link = document.createElement("a");
        link.href = image;
        link.download = "amount_of_drops.png"; // Set the default file name
        link.click(); // Programmatically click the link to trigger download
      });
    };

    const effectiveSeries = cumulativeUsersOverTime ?? [];
    const exportPeriod = useFromToFilters ? fromToPeriod : period;

    const downloadCsv = () => {
      const series = effectiveSeries ?? [];
      if (!series.length) return;
      setIsCsvDownloading(true);
      if (useFromToFilters) {
        const buildDailyParams = () => {
          if (fromToPeriod === "week") {
            const startDate = dayjs()
              .year(startYear)
              .isoWeek(startWeek)
              .startOf("isoWeek");
            const endDate = dayjs()
              .year(endYear)
              .isoWeek(endWeek)
              .endOf("isoWeek");
            return {
              params: {
                period: "day",
                startYear: startDate.year(),
                startMonth: startDate.month() + 1,
                endYear: endDate.year(),
                endMonth: endDate.month() + 1,
                ...(selectedFiltersProp?.length &&
                !selectedFiltersProp.includes("all")
                  ? { selectedStreams: selectedFiltersProp.join(",") }
                  : {}),
                ...(selectedStoresProp?.length &&
                !selectedStoresProp.includes("all")
                  ? { selectedStores: selectedStoresProp.join(",") }
                  : {}),
              },
              startTs: startDate.startOf("day").valueOf(),
              endTs: endDate.endOf("day").valueOf(),
            };
          }

          return {
            params: {
              period: "day",
              startYear,
              startMonth:
                fromToPeriod === "year"
                  ? 1
                  : fromToPeriod === "quarter"
                    ? (startQuarter - 1) * 3 + 1
                    : startMonth,
              endYear,
              endMonth:
                fromToPeriod === "year"
                  ? 12
                  : fromToPeriod === "quarter"
                    ? endQuarter * 3
                    : endMonth,
              ...(selectedFiltersProp?.length &&
              !selectedFiltersProp.includes("all")
                ? { selectedStreams: selectedFiltersProp.join(",") }
                : {}),
              ...(selectedStoresProp?.length &&
              !selectedStoresProp.includes("all")
                ? { selectedStores: selectedStoresProp.join(",") }
                : {}),
            },
          };
        };

        const dailyConfig = buildDailyParams();
        axios
          .get("/users/new-stats/cumulative-users-over-time", {
            params: dailyConfig.params,
          })
          .then((response) => {
            const { startTs, endTs } = dailyConfig;
            const dailySeries = ((response.data as typeof effectiveSeries) ?? []).map(
              (item: any) => ({
                ...item,
                data:
                  ((item.data as [number, number][]) ?? []).filter(
                    ([ts]) =>
                      (startTs == null || ts >= startTs) &&
                      (endTs == null || ts <= endTs)
                  ),
              })
            );
            const allTs = new Set<number>();
            dailySeries.forEach((s: any) => {
              const d = (s.data as [number, number][]) ?? [];
              d.forEach(([ts]) => allTs.add(ts));
            });
            const sortedTs = Array.from(allTs).sort((a, b) => a - b);
            const header =
              "Date," +
              dailySeries
                .map((s: any) => String(s?.name ?? "").replace(/,/g, " "))
                .join(",") +
              "\n";
            const rows = sortedTs.map((ts) => {
              const values = dailySeries.map((s: any) => {
                const d = (s.data as [number, number][]) ?? [];
                const pair = d.find(([t]) => t === ts);
                return pair != null ? (pair[1] ?? "").toString() : "";
              });
              return [dayjs(ts).format("YYYY-MM-DD"), ...values].join(",");
            });
            const csv = header + rows.join("\n");
            const blob = new Blob([csv], {
              type: "text/csv;charset=utf-8;",
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `cumulative_users_over_time_daily_${dayjs().format(
              "YYYY-MM-DD"
            )}.csv`;
            link.click();
            URL.revokeObjectURL(url);
          })
          .catch((error) => {
            console.error(
              "Error downloading CSV for CumulativeUsersOverTime:",
              error
            );
          })
          .finally(() => {
            setIsCsvDownloading(false);
          });
        return;
      }

      try {
        const allTs = new Set<number>();
        series.forEach((s: any) => {
          const d = (s.data as unknown as [number, number][] | undefined) ?? [];
          d.forEach(([ts]) => allTs.add(ts));
        });
        const sortedTs = Array.from(allTs).sort((a, b) => a - b);

        const header =
          "Date," +
          series
            .map((s: any) => String(s?.name ?? "").replace(/,/g, " "))
            .join(",") +
          "\n";

        const rows = sortedTs.map((ts) => {
          const dateLabel =
            exportPeriod === "year"
              ? dayjs(ts).format("YYYY")
              : exportPeriod === "quarter"
                ? `Q${Math.ceil((dayjs(ts).month() + 1) / 3)} ${dayjs(ts).year()}`
                : exportPeriod === "week"
                  ? `${dayjs(ts).year()}-W${String(
                      dayjs(ts).isoWeek()
                    ).padStart(2, "0")}`
                  : dayjs(ts).format("MMM YYYY");

          const values = series.map((s: any) => {
            const d =
              (s.data as unknown as [number, number][] | undefined) ?? [];
            const pair = d.find(([t]) => t === ts);
            return pair != null ? (pair[1] ?? "").toString() : "";
          });

          return [dateLabel, ...values].join(",");
        });

        const csv = header + rows.join("\n");
        const blob = new Blob([csv], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `cumulative_users_over_time_${dayjs().format(
          "YYYY-MM-DD"
        )}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          "Error downloading CSV for CumulativeUsersOverTime:",
          error
        );
      } finally {
        setIsCsvDownloading(false);
      }
    };

    const getLastWeek = () =>
      setWeek(new Date(week.setDate(week.getDate() - 7)));
    const getNextWeek = () =>
      setWeek(new Date(week.setDate(week.getDate() + 7)));

    /** Effects */
    useEffect(() => {
      if (useFromToFilters) {
        let apiStartYear: number;
        let apiStartMonth: number;
        let apiEndYear: number;
        let apiEndMonth: number;
        if (fromToPeriod === "week") {
          const startDate = dayjs()
            .year(startYear)
            .isoWeek(startWeek)
            .startOf("isoWeek");
          const endDate = dayjs()
            .year(endYear)
            .isoWeek(endWeek)
            .endOf("isoWeek");
          apiStartYear = startDate.year();
          apiStartMonth = startDate.month() + 1;
          apiEndYear = endDate.year();
          apiEndMonth = endDate.month() + 1;
        } else if (fromToPeriod === "quarter") {
          apiStartYear = startYear;
          apiStartMonth = (startQuarter - 1) * 3 + 1;
          apiEndYear = endYear;
          apiEndMonth = endQuarter * 3;
        } else if (fromToPeriod === "year") {
          apiStartYear = startYear;
          apiStartMonth = 1;
          apiEndYear = endYear;
          apiEndMonth = 12;
        } else {
          apiStartYear = startYear;
          apiStartMonth = startMonth;
          apiEndYear = endYear;
          apiEndMonth = endMonth;
        }
        getCumulativeUsersOverTime({
          period: fromToPeriod,
          fromToParams: {
            period: fromToPeriod,
            startYear: apiStartYear,
            startMonth: apiStartMonth,
            endYear: apiEndYear,
            endMonth: apiEndMonth,
          },
          ignoreLocation: true,
        });
        return;
      }
      if (period === "all")
        getCumulativeUsersOverTime({
          period,
          selectedFilters: selectedFiltersProp,
          selectedStores: selectedStoresProp,
        });
      if (period === "yearly")
        getCumulativeUsersOverTime({
          period,
          year,
          selectedFilters: selectedFiltersProp,
          selectedStores: selectedStoresProp,
        });
      if (period === "quarterly")
        getCumulativeUsersOverTime({
          period,
          year,
          quarter,
          selectedFilters: selectedFiltersProp,
          selectedStores: selectedStoresProp,
        });
      if (period === "monthly")
        getCumulativeUsersOverTime({
          period,
          year,
          month,
          selectedFilters: selectedFiltersProp,
          selectedStores: selectedStoresProp,
        });
      if (period === "weekly")
        getCumulativeUsersOverTime({
          period,
          year,
          month,
          week,
          selectedFilters: selectedFiltersProp,
          selectedStores: selectedStoresProp,
        });
    }, [
      useFromToFilters,
      fromToPeriod,
      startYear,
      startMonth,
      startWeek,
      startQuarter,
      endYear,
      endMonth,
      endWeek,
      endQuarter,
      period,
      year,
      quarter,
      month,
      week,
      selectedFiltersProp,
      selectedStoresProp,
    ]);

    useEffect(() => {
      if (!useFromToFilters) {
        setPeriod(periodProp);
        setYear(yearProp);
        setQuarter(quarterProp);
        setMonth(monthProp);
        setWeek(weekProp);
      }
    }, [
      useFromToFilters,
      periodProp,
      yearProp,
      quarterProp,
      monthProp,
      weekProp,
    ]);

    const monthlyTickAmount = useMemo(() => {
      if (!cumulativeUsersOverTime) return 11;
      const values = Object.values(cumulativeUsersOverTime) as any[];
      return values?.[0]?.data?.length - 1 || 11;
    }, [cumulativeUsersOverTime]);

    const displayPeriod = useFromToFilters ? fromToPeriod : period;

    const getTargetAnnotations = useMemo(() => {
      if (!showTargets) return [];
      return [
        {
          y: downloadsTarget,
          borderColor: "#FFB323",
          borderWidth: 2,
          strokeDashArray: 8,
          label: {
            borderColor: "#FFB323",
            style: {
              color: "#fff",
              background: "#FFB323",
            },
            text: `Downloads Target (${new Intl.NumberFormat("nl-NL").format(downloadsTarget)})`,
          },
        },
        {
          y: regularDroppersTarget,
          borderColor: "#8A2BE2",
          borderWidth: 2,
          strokeDashArray: 8,
          label: {
            borderColor: "#8A2BE2",
            style: {
              color: "#fff",
              background: "#8A2BE2",
            },
            text: `Regular Droppers Target (${new Intl.NumberFormat("nl-NL").format(regularDroppersTarget)})`,
          },
        },
      ];
    }, [showTargets, downloadsTarget, regularDroppersTarget]);

    return (
      <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl shadow-none mb-1 sm:mb-10">
        <div className="flex flex-col sm:flex-row w-full items-start justify-between">
          <div></div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap w-full min-w-0 items-start sm:items-center justify-start sm:justify-end gap-2 sm:gap-3">
            {useFromToFilters ? (
              <>
                <div className="flex items-center gap-1 sm:gap-2">
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                        {fromToPeriod === "month"
                          ? "Monthly"
                          : fromToPeriod === "week"
                            ? "Weekly"
                            : fromToPeriod === "quarter"
                              ? "Quarterly"
                              : "Yearly"}
                        <ChevronDown className="ml-1 sm:ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {(["month", "week", "quarter", "year"] as const).map(
                        (p) => (
                          <MenuItem
                            key={p}
                            disabled={fromToPeriod === p}
                            onClick={() => setFromToPeriod(p)}
                            className="font-satoshi text-base text-dark"
                            placeholder={undefined}
                          >
                            {p === "month"
                              ? "Monthly"
                              : p === "week"
                                ? "Weekly"
                                : p === "quarter"
                                  ? "Quarterly"
                                  : "Yearly"}
                          </MenuItem>
                        )
                      )}
                    </MenuList>
                  </Menu>
                </div>
                <div className="flex items-center gap-2">
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                        {startYear}
                        <ChevronDown className="ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {yearOptions.map((y) => (
                        <MenuItem
                          key={y}
                          disabled={startYear === y}
                          onClick={() => setStartYear(y)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {y}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                  {fromToPeriod === "month" && (
                    <Menu>
                      <MenuHandler>
                        <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                          {startMonthOptions.find((m) => m.value === startMonth)
                            ?.label || "Month"}
                          <ChevronDown className="ml-2" />
                        </button>
                      </MenuHandler>
                      <MenuList
                        className="rounded-none"
                        placeholder={undefined}
                      >
                        {startMonthOptions.map((m) => (
                          <MenuItem
                            key={m.value}
                            disabled={startMonth === m.value}
                            onClick={() => setStartMonth(m.value)}
                            className="font-satoshi text-base text-dark"
                            placeholder={undefined}
                          >
                            {m.label}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  )}
                  {fromToPeriod === "week" && (
                    <Menu>
                      <MenuHandler>
                        <button
                          className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                          title={
                            startWeekOptions.find((w) => w.value === startWeek)
                              ?.label
                          }
                        >
                          {startWeekOptions.find((w) => w.value === startWeek)
                            ?.label || "Week"}
                          <ChevronDown className="ml-1 sm:ml-2" />
                        </button>
                      </MenuHandler>
                      <MenuList
                        className="rounded-none max-h-[280px] overflow-y-auto"
                        placeholder={undefined}
                      >
                        {startWeekOptions.map((w) => (
                          <MenuItem
                            key={w.value}
                            disabled={startWeek === w.value}
                            onClick={() => setStartWeek(w.value)}
                            className="font-satoshi text-base text-dark"
                            placeholder={undefined}
                          >
                            {w.label}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  )}
                  {fromToPeriod === "quarter" && (
                    <Menu>
                      <MenuHandler>
                        <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                          {
                            quarterOptions.find((q) => q.value === startQuarter)
                              ?.label
                          }
                          <ChevronDown className="ml-1 sm:ml-2" />
                        </button>
                      </MenuHandler>
                      <MenuList
                        className="rounded-none"
                        placeholder={undefined}
                      >
                        {quarterOptions.map((q) => (
                          <MenuItem
                            key={q.value}
                            disabled={startQuarter === q.value}
                            onClick={() => setStartQuarter(q.value)}
                            className="font-satoshi text-base text-dark"
                            placeholder={undefined}
                          >
                            {q.label}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <label className="font-satoshi text-sm text-gray-text">
                    To:
                  </label>
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                        {endYear}
                        <ChevronDown className="ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {yearOptions.map((y) => (
                        <MenuItem
                          key={y}
                          disabled={endYear === y}
                          onClick={() => setEndYear(y)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {y}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                  {fromToPeriod === "month" && (
                    <Menu>
                      <MenuHandler>
                        <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                          {endMonthOptions.find((m) => m.value === endMonth)
                            ?.label || "Month"}
                          <ChevronDown className="ml-2" />
                        </button>
                      </MenuHandler>
                      <MenuList
                        className="rounded-none"
                        placeholder={undefined}
                      >
                        {endMonthOptions.map((m) => (
                          <MenuItem
                            key={m.value}
                            disabled={endMonth === m.value}
                            onClick={() => setEndMonth(m.value)}
                            className="font-satoshi text-base text-dark"
                            placeholder={undefined}
                          >
                            {m.label}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  )}
                  {fromToPeriod === "week" && (
                    <Menu>
                      <MenuHandler>
                        <button
                          className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                          title={
                            endWeekOptions.find((w) => w.value === endWeek)
                              ?.label
                          }
                        >
                          {endWeekOptions.find((w) => w.value === endWeek)
                            ?.label || "Week"}
                          <ChevronDown className="ml-1 sm:ml-2" />
                        </button>
                      </MenuHandler>
                      <MenuList
                        className="rounded-none max-h-[280px] overflow-y-auto"
                        placeholder={undefined}
                      >
                        {endWeekOptions.map((w) => (
                          <MenuItem
                            key={w.value}
                            disabled={endWeek === w.value}
                            onClick={() => setEndWeek(w.value)}
                            className="font-satoshi text-base text-dark"
                            placeholder={undefined}
                          >
                            {w.label}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  )}
                  {fromToPeriod === "quarter" && (
                    <Menu>
                      <MenuHandler>
                        <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                          {
                            quarterOptions.find((q) => q.value === endQuarter)
                              ?.label
                          }
                          <ChevronDown className="ml-1 sm:ml-2" />
                        </button>
                      </MenuHandler>
                      <MenuList
                        className="rounded-none"
                        placeholder={undefined}
                      >
                        {quarterOptions.map((q) => (
                          <MenuItem
                            key={q.value}
                            disabled={endQuarter === q.value}
                            onClick={() => setEndQuarter(q.value)}
                            className="font-satoshi text-base text-dark"
                            placeholder={undefined}
                          >
                            {q.label}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  )}
                </div>
              </>
            ) : (
              <>
                {period === "weekly" ? (
                  <div className="flex flex-col sm:flex-row items-start sm:items-center mr-0 sm:mr-[50px]  sm:mb-0">
                    <PrimaryButton
                      size="sm"
                      onClick={getLastWeek}
                      label="Prev"
                    />
                    <div className="mx-1 sm:mx-4 font-satoshi font-medium my-1 sm:my-0">
                      {startOfWeek.toDateString()} - {endOfWeek.toDateString()}
                    </div>
                    <PrimaryButton
                      size="sm"
                      disabled={endOfWeek > new Date()}
                      onClick={getNextWeek}
                      label="Next"
                    />
                  </div>
                ) : null}
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {period.charAt(0).toUpperCase() + period.slice(1)}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    <MenuItem
                      disabled={period === "all"}
                      onClick={() => setPeriod && setPeriod("all")}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      All
                    </MenuItem>
                    <MenuItem
                      disabled={period === "weekly"}
                      onClick={() => setPeriod && setPeriod("weekly")}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      Weekly
                    </MenuItem>
                    <MenuItem
                      disabled={period === "monthly"}
                      onClick={() => setPeriod && setPeriod("monthly")}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      Monthly
                    </MenuItem>
                    <MenuItem
                      disabled={period === "quarterly"}
                      onClick={() => setPeriod && setPeriod("quarterly")}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      Quarterly
                    </MenuItem>
                    <MenuItem
                      disabled={period === "yearly"}
                      onClick={() => setPeriod && setPeriod("yearly")}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      Yearly
                    </MenuItem>
                  </MenuList>
                </Menu>
                {period !== "all" && period !== "weekly" ? (
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-3 ml-4 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                        {year}
                        <ChevronDown className="ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {Array.from(
                        { length: currentYear - 2020 },
                        (_, i) => currentYear - i
                      ).map((y) => (
                        <MenuItem
                          key={y}
                          onClick={() => setYear && setYear(y)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                          disabled={year === y}
                        >
                          {y}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                ) : null}
                {period === "monthly" ? (
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-3 ml-4 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                        {dayjs().month(month).format("MMMM")}
                        <ChevronDown className="ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((m) => (
                        <MenuItem
                          key={m}
                          onClick={() => setMonth && setMonth(m)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                          disabled={month === m}
                        >
                          {dayjs().month(m).format("MMMM")}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                ) : null}
                {period === "quarterly" ? (
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-3 ml-4 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                        {quarter}
                        <ChevronDown className="ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {["Q1", "Q2", "Q3", "Q4"].map((q) => (
                        <MenuItem
                          key={q}
                          onClick={() =>
                            setQuarter &&
                            setQuarter(q as "Q1" | "Q2" | "Q3" | "Q4")
                          }
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                          disabled={quarter === q}
                        >
                          {q}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                ) : null}
              </>
            )}
            <div className="ml-0 sm:ml-2" />

            <PrimaryButton
              size="sm"
              onClick={downloadCsv}
              disabled={!effectiveSeries?.length || isCsvDownloading}
              label={isCsvDownloading ? "..." : "Download CSV"}
              className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
            />
            <PrimaryButton
              size="sm"
              onClick={downloadScreenshot}
              label="Download image"
              className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
            />
          </div>
        </div>

        <section ref={snapshotRef} className="mt-4">
          <div className="flex items-center mb-2 ">
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
              Cumulative users over time
            </p>
            <Tooltip
              content="Cumulative: Shows running total of users that grows over time"
              placeholder={undefined}
            >
              <img
                src={InformationIcon}
                alt="info"
                className="inline-block w-4 h-4 ml-2 cursor-pointer"
              />
            </Tooltip>
          </div>
          {showTargets && (
            <div className="flex flex-col sm:flex-row w-full items-start justify-end mb-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                <div className="flex items-center">
                  <label className="font-satoshi text-sm text-gray-text mr-2">
                    Downloads Target:
                  </label>
                  <input
                    type="number"
                    value={downloadsTarget}
                    onChange={(e) =>
                      setDownloadsTarget(Number(e.target.value) || 0)
                    }
                    className="h-8 px-3 border rounded border-gray-border bg-white font-satoshi font-medium text-gray-text text-sm w-24"
                    placeholder="23500"
                    min="0"
                  />
                </div>
                <div className="flex items-center">
                  <label className="font-satoshi text-sm text-gray-text mr-2">
                    Regular Droppers Target:
                  </label>
                  <input
                    type="number"
                    value={regularDroppersTarget}
                    onChange={(e) =>
                      setRegularDroppersTarget(Number(e.target.value) || 0)
                    }
                    className="h-8 px-3 border rounded border-gray-border bg-white font-satoshi font-medium text-gray-text text-sm w-24"
                    placeholder="13000"
                    min="0"
                  />
                </div>
              </div>
            </div>
          )}
          <div>
            {isCumulativeUsersOverTimeLoading ||
            cumulativeUsersOverTime === null ? (
              <div className="flex items-center justify-center h-[300px] w-full">
                <svg
                  aria-hidden="true"
                  className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-primary-base"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
              </div>
            ) : (
              <>
                {cumulativeUsersOverTime &&
                (useFromToFilters || period === "all") ? (
                  <Chart
                    // @ts-ignore
                    options={{
                      ...options,
                      xaxis: {
                        tickAmount:
                          cumulativeUsersOverTime?.[0]?.data?.length - 1,
                        labels: {
                          // @ts-ignore
                          formatter: function (value: number) {
                            if (useFromToFilters) {
                              if (fromToPeriod === "year")
                                return dayjs(value).format("YYYY");
                              if (fromToPeriod === "quarter") {
                                const d = dayjs(value);
                                const q = Math.ceil((d.month() + 1) / 3);
                                return `Q${q} ${d.year()}`;
                              }
                              if (fromToPeriod === "week")
                                return dayjs(value).format("MMM DD");
                              return dayjs(value).format("MMM YYYY");
                            }
                            return dayjs(value).format("MMM YYYY");
                          },
                        },
                      },
                      annotations: {
                        yaxis: getTargetAnnotations,
                      },
                    }}
                    series={cumulativeUsersOverTime}
                    type="area"
                    height={300}
                  />
                ) : null}
                {cumulativeUsersOverTime &&
                !useFromToFilters &&
                period === "weekly" ? (
                  <Chart
                    // @ts-ignore
                    options={{
                      ...options,
                      xaxis: {
                        type: "datetime",
                        tickAmount: 6,
                        labels: {
                          // @ts-ignore
                          formatter: function (value: number) {
                            return dayjs(value).format("ddd");
                          },
                        },
                      },
                      annotations: {
                        yaxis: getTargetAnnotations,
                      },
                    }}
                    series={cumulativeUsersOverTime}
                    type="area"
                    height={300}
                  />
                ) : null}
                {cumulativeUsersOverTime &&
                !useFromToFilters &&
                period === "monthly" ? (
                  <Chart
                    options={{
                      ...options,
                      xaxis: {
                        // Only show 1 tick when there's only one month
                        // @ts-ignore
                        tickAmount: monthlyTickAmount,
                        labels: {
                          // @ts-ignore
                          formatter: function (value: number) {
                            return dayjs(value).format("DD MMM");
                          },
                        },
                      },
                      annotations: {
                        yaxis: getTargetAnnotations,
                      },
                    }}
                    series={cumulativeUsersOverTime}
                    type="area"
                    height={300}
                  />
                ) : null}
                {cumulativeUsersOverTime &&
                !useFromToFilters &&
                period === "quarterly" ? (
                  <Chart
                    // @ts-ignore
                    options={{
                      ...options,
                      xaxis: {
                        tickAmount: 2,
                        labels: {
                          // @ts-ignore
                          formatter: function (value: number) {
                            return dayjs(value).format("MMM");
                          },
                        },
                      },
                      annotations: {
                        yaxis: getTargetAnnotations,
                      },
                    }}
                    series={cumulativeUsersOverTime}
                    type="area"
                    height={300}
                  />
                ) : null}
                {cumulativeUsersOverTime &&
                !useFromToFilters &&
                period === "yearly" ? (
                  <Chart
                    // @ts-ignore
                    options={{
                      ...options,
                      xaxis: {
                        tickAmount:
                          cumulativeUsersOverTime?.[0]?.data?.length - 1,
                        labels: {
                          // @ts-ignore
                          formatter: function (value: number) {
                            return dayjs(value).format("MMM");
                          },
                        },
                      },
                      annotations: {
                        yaxis: getTargetAnnotations,
                      },
                    }}
                    series={cumulativeUsersOverTime}
                    type="area"
                    height={300}
                  />
                ) : null}
              </>
            )}
          </div>
        </section>
      </div>
    );
  }
);

const options = {
  chart: {
    type: "area",
    zoom: {
      enabled: false,
    },
    toolbar: {
      show: false,
    },
  },
  dataLabels: {
    enabled: false,
  },
  fill: {
    type: "gradient",
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.7,
      opacityTo: 0.3,
      stops: [0, 100],
    },
  },
  stroke: {
    curve: "smooth",
    width: 2,
  },
  grid: {
    strokeDashArray: 5,
  },
  markers: {
    size: 1,
  },
  tooltip: {
    shared: true,
    intersect: false,
    custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
      // Find the "All downloads" series value
      let allDownloadsValue = 0;
      const allDownloadsSeriesIndex = w.config.series.findIndex(
        (s: any) => s.name && s.name.toLowerCase() === "all downloads"
      );

      if (
        allDownloadsSeriesIndex !== -1 &&
        w.config.series[allDownloadsSeriesIndex].data
      ) {
        const dataPoint =
          w.config.series[allDownloadsSeriesIndex].data[dataPointIndex];
        allDownloadsValue =
          typeof dataPoint === "object"
            ? dataPoint.y
            : Array.isArray(dataPoint)
              ? dataPoint[1]
              : dataPoint;
      }

      // Create an array of series with their values
      const seriesData = series.map((seriesValues: any, index: number) => ({
        name: w.globals.seriesNames[index],
        value: seriesValues[dataPointIndex],
        color: w.config.colors[index],
      }));

      // Sort by value in descending order (highest to lowest)
      seriesData.sort((a: any, b: any) => b.value - a.value);

      // Build the tooltip HTML with original ApexCharts styling
      let tooltipHtml =
        '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">';
      tooltipHtml += `${dayjs(
        w.globals.seriesX[seriesIndex][dataPointIndex]
      ).format("MMM DD, YYYY")}</div>`;

      seriesData.forEach((item: any) => {
        const percentage =
          allDownloadsValue > 0
            ? ((item.value / allDownloadsValue) * 100).toFixed(1)
            : "100";

        tooltipHtml += `
          <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
            <span class="apexcharts-tooltip-marker" style="background-color: ${
              item.color
            };"></span>
            <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
              <div class="apexcharts-tooltip-y-group">
                <span class="apexcharts-tooltip-text-y-label">${
                  item.name
                }: </span>
                <span class="apexcharts-tooltip-text-y-value">${item.value.toLocaleString()} (${percentage}%)</span>
              </div>
            </div>
          </div>
        `;
      });

      return tooltipHtml;
    },
  },
  colors: [
    "#FFB323", // Yellow
    "#00CED1", // DarkTurquoise
    "#8A2BE2", // BlueViolet
    "#23C16B", // Green
    "#48A7F8", // Blue
    "#FF3333", // Red
    "#FF8800", // Orange
    "#9259FF", // Purple
    "#FF4DA6", // Pink
    "#33FFE6", // Teal
    "#808080", // Gray
    "#D17A50", // Brown
    "#FF7F50", // Coral
    "#5F9EA0", // CadetBlue
    "#DC143C", // Crimson
    "#00FFFF", // Cyan
    "#FF1493", // DeepPink
    "#696969", // DimGray
    "#B8860B", // DarkGoldenrod
    "#FF4500", // OrangeRed
    "#32CD32", // LimeGreen
    "#4682B4", // SteelBlue
    "#00FA9A", // MediumSpringGreen
    "#FFD700", // Gold
    "#FF6347", // Tomato
    "#7B68EE", // MediumSlateBlue
    "#008080", // Teal
    "#6A5ACD", // SlateBlue
    "#D2691E", // Chocolate
    "#B22222", // FireBrick
    "#9ACD32", // YellowGreen
    "#8B008B", // DarkMagenta
    "#FF69B4", // HotPink
    "#1E90FF", // DodgerBlue
    "#40E0D0", // Turquoise
    "#EE82EE", // Violet
  ],
};
